import { useCallback, useEffect, useRef, useState } from "react";
import { getWaitingOnWorkItems } from "../service/inbox";

/**
 * Work-items context for the open document's matter.
 *
 * One fetch feeds three consumers: the verdict bar (`linked` — open
 * items this document already answers), the WaitingOnBanner (`items` —
 * open items expecting something inbound, inbox only), and the inbox
 * action footer's one-door gate (a migrated matter hides the
 * Litify-bound Create Task — the Work Items loop is the one door for
 * accountable work there; unmigrated matters keep Create Task, which
 * still reaches Litify). Failures resolve to null: no banner, no
 * verdict bar, no hiding — the hint is never a gate.
 *
 * Shared by the desktop AIPane (inbox mode), MobileInbox, and the
 * matter workspace (MatterDetails), which asks once per document and
 * hands the answer to both the AI pane's verdict bar and the document
 * toolbar's pill so the two never disagree (Bugbot, PR #636).
 * Responses are sequence-guarded: the reader can flip documents faster
 * than Salesforce answers, and a stale response must never masquerade
 * as the current document's context. The result also remembers which
 * document it belongs to, so the render right after a document switch
 * reads null rather than the previous document's rows (Bugbot, PR
 * #905: an effect keyed on the row count would otherwise open the
 * matter pane for a document that has no work item).
 *
 * @param {string|number|null} documentId — pass null/undefined to
 *   disable (e.g. no document is open).
 * @returns {{ waitingOn: object|null, reload: () => void }}
 */
const useWaitingOnWorkItems = (documentId) => {
  const [result, setResult] = useState({ documentId: null, data: null });
  const requestSeq = useRef(0);

  const reload = useCallback(() => {
    const seq = ++requestSeq.current;
    if (documentId == null) {
      setResult({ documentId: null, data: null });
      return;
    }
    getWaitingOnWorkItems(documentId)
      .then((response) => {
        if (seq !== requestSeq.current) return;
        setResult({ documentId, data: response?.data || null });
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setResult({ documentId, data: null });
      });
  }, [documentId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const waitingOn =
    documentId != null && result.documentId === documentId ? result.data : null;

  return { waitingOn, reload };
};

export default useWaitingOnWorkItems;
